import React, { useState, useMemo, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import "../../styles/manage-user-css/users.css";
import axiosConfig from "../../axiosConfig";
import { FaTimes, FaEye, FaEyeSlash } from "react-icons/fa";

const SEARCH_FIELDS = [
  { value: "all", label: "Search All Fields" },
  { value: "name", label: "Name" },
  { value: "email", label: "Email" },
  { value: "role", label: "Role" },
  { value: "organization", label: "Organization" },
];

const EMPTY_FORM = {
  name: "",
  email: "",
  password: "",
  country_id: "",
  role: "viewer",
  status: "active",
  organization: "",
  designation: "",
};

export default function User() {
  const [users, setUsers] = useState([]);
  const [countries, setCountries] = useState([]);
  const [viewedUser, setViewedUser] = useState(null);
  const [formMode, setFormMode] = useState(null);
  const [formUserId, setFormUserId] = useState(null);
  const [formData, setFormData] = useState(EMPTY_FORM);
  const [formError, setFormError] = useState("");
  const [formSaving, setFormSaving] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [searchField, setSearchField] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [searchInput, setSearchInput] = useState("");
  const [entriesPerPage, setEntriesPerPage] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  const authHeaders = useCallback(
    () => ({ Authorization: `Bearer ${localStorage.getItem("token")}` }),
    []
  );

  const fetchUsers = useCallback(() => {
    axiosConfig
      .get("/user", { headers: authHeaders() })
      .then((res) => {
        setUsers(Array.isArray(res.data) ? res.data : res.data.users || []);
      })
      .catch((err) => {
        console.error("Failed to fetch users:", err);
        setUsers([]);
      });
  }, [authHeaders]);

  useEffect(() => {
    fetchUsers();
    axiosConfig
      .get("/countries", { headers: authHeaders() })
      .then((res) => setCountries(Array.isArray(res.data) ? res.data : []))
      .catch(() => setCountries([]));
  }, [fetchUsers, authHeaders]);

  const matchesField = (user, field, term) => {
    const value = (user[field] || "").toString().toLowerCase();
    return value.includes(term);
  };

  const filteredUsers = useMemo(() => {
    const term = searchInput.trim().toLowerCase();
    return users.filter((user) => {
      const status = (user.status || "").toLowerCase();
      if (statusFilter !== "all" && status !== statusFilter) return false;
      if (!term) return true;
      if (searchField === "all") {
        return ["name", "email", "role", "organization", "designation"].some((field) =>
          matchesField(user, field, term)
        );
      }
      return matchesField(user, searchField, term);
    });
  }, [users, searchInput, searchField, statusFilter]);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchInput, searchField, statusFilter]);

  const totalPages = Math.max(1, Math.ceil(filteredUsers.length / entriesPerPage));
  const indexOfLastEntry = currentPage * entriesPerPage;
  const indexOfFirstEntry = indexOfLastEntry - entriesPerPage;
  const currentEntries = filteredUsers.slice(indexOfFirstEntry, indexOfLastEntry);

  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  const getHandle = (user) => {
    if (user.email && user.email.includes("@")) return `@${user.email.split("@")[0]}`;
    return user.organization ? `@${user.organization.replace(/\s+/g, "").toLowerCase()}` : "—";
  };

  const formatDate = (value) => {
    if (!value) return "—";
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return "—";
    return date.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
  };

  const formatLastAccess = (user) => {
    const value = user.last_access || user.last_login || user.last_login_at;
    if (!value) return "—";
    return formatDate(value);
  };

  const formatLabel = (value) => {
    const text = String(value || "").trim();
    if (!text) return "—";
    return text.charAt(0).toUpperCase() + text.slice(1).toLowerCase();
  };

  const roleChipClass = (role) => {
    const r = (role || "").toLowerCase();
    return r === "admin" ? "chip chip-lilac" : "chip chip-primary";
  };

  const statusChipClass = (status) => {
    const s = (status || "").toLowerCase();
    return s === "active" ? "chip chip-mint" : "chip chip-peach";
  };

  const isViewerRole = (role) => (role || "").toLowerCase() === "viewer";

  const assignedBookCount = (user) => {
    if (!isViewerRole(user.role)) return "—";
    const count = Number(user.assigned_books_count);
    return Number.isFinite(count) ? count : 0;
  };

  const handleDeleteUser = async (userId) => {
    const confirmDelete = window.confirm("Are you sure you want to delete this user? This action cannot be undone.");
    if (!confirmDelete) return;

    try {
      const token = localStorage.getItem("token");
      setIsLoading(true);
      setError(null);
      await axiosConfig.delete(`/user/${userId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setUsers((prevUsers) => prevUsers.filter((user) => user.user_id !== userId));
      alert("User deleted successfully!");
      if (currentEntries.length === 1 && currentPage > 1) {
        setCurrentPage((prev) => prev - 1);
      }
    } catch (err) {
      console.error("Failed to delete user:", err);
      setError(err.response?.data?.error || "Failed to delete user. Please try again.");
      alert("Failed to delete user. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleSuspendUser = async (user) => {
    const nextStatus = (user.status || "").toLowerCase() === "active" ? "inactive" : "active";
    const actionLabel = nextStatus === "inactive" ? "suspend" : "activate";
    const confirmed = window.confirm(`Are you sure you want to ${actionLabel} ${user.name || "this user"}?`);
    if (!confirmed) return;

    try {
      const token = localStorage.getItem("token");
      setIsLoading(true);
      await axiosConfig.put(
        `/user/${user.user_id}`,
        {
          name: user.name,
          email: user.email,
          role: user.role,
          status: nextStatus,
          organization: user.organization || "",
          designation: user.designation || "",
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setUsers((prev) =>
        prev.map((item) => (item.user_id === user.user_id ? { ...item, status: nextStatus } : item))
      );
    } catch (err) {
      console.error("Failed to update user status:", err);
      alert(err.response?.data?.error || "Failed to update user status.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleViewUser = async (userId) => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await axiosConfig.get(`/user/${userId}`, { headers: authHeaders() });
      setViewedUser(response.data);
    } catch (err) {
      console.error("Failed to fetch user details:", err);
      setError(err.response?.data?.error || "Failed to fetch user details");
      setViewedUser({ user_id: userId });
    } finally {
      setIsLoading(false);
    }
  };

  const closeForm = () => {
    setFormMode(null);
    setFormUserId(null);
    setFormData(EMPTY_FORM);
    setFormError("");
    setShowPassword(false);
    setFormSaving(false);
  };

  const openAddUser = () => {
    setViewedUser(null);
    setFormMode("add");
    setFormUserId(null);
    setFormData(EMPTY_FORM);
    setFormError("");
    setShowPassword(false);
  };

  const openEditUser = async (userId) => {
    setViewedUser(null);
    setFormMode("edit");
    setFormUserId(userId);
    setFormError("");
    setShowPassword(false);
    setFormSaving(true);
    try {
      const response = await axiosConfig.get(`/user/${userId}`, { headers: authHeaders() });
      const user = response.data || {};
      setFormData({
        name: user.name || "",
        email: user.email || "",
        password: "",
        country_id: user.country_id || "",
        role: user.role || "viewer",
        status: user.status || "active",
        organization: user.organization || "",
        designation: user.designation || "",
      });
    } catch (err) {
      setFormError(err.response?.data?.error || "Failed to load user details");
    } finally {
      setFormSaving(false);
    }
  };

  const handleFormChange = (event) => {
    const { name, value } = event.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleFormSubmit = async (event) => {
    event.preventDefault();
    setFormError("");
    setFormSaving(true);
    try {
      if (formMode === "add") {
        await axiosConfig.post("/user/register", formData, { headers: authHeaders() });
        alert("User registered successfully!");
      } else {
        const payload = { ...formData };
        if (!payload.password.trim()) delete payload.password;
        await axiosConfig.put(`/user/${formUserId}`, payload, { headers: authHeaders() });
        alert("User updated successfully!");
      }
      closeForm();
      fetchUsers();
    } catch (err) {
      setFormError(err.response?.data?.error || (formMode === "add" ? "Registration failed" : "Failed to update user"));
    } finally {
      setFormSaving(false);
    }
  };

  const navBtnStyle = { width: "auto", minWidth: 32, padding: "0 12px" };

  return (
    <div className="users-page-wrapper users-list-page">
      <div className="page-head">
        <div>
          <h1 className="page-title">Users</h1>
          <p className="page-desc">Everyone with access to the shared-book admin console.</p>
        </div>
        <button type="button" className="btn btn-primary" onClick={openAddUser}>
          + Add New User
        </button>
      </div>

      <div className="panel" style={{ marginBottom: 18 }}>
        <div className="panel-title" style={{ marginBottom: 14 }}>
          Search users
        </div>
        <div className="field-row" style={{ marginBottom: 0 }}>
          <div className="field">
            <label>Search field</label>
            <select value={searchField} onChange={(e) => setSearchField(e.target.value)}>
              {SEARCH_FIELDS.map((field) => (
                <option key={field.value} value={field.value}>
                  {field.label}
                </option>
              ))}
            </select>
          </div>
          <div className="field">
            <label>Status</label>
            <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
              <option value="all">All Status</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </select>
          </div>
          <div className="field" style={{ flex: 1 }}>
            <label>Search</label>
            <input
              type="text"
              className="text-input grow"
              placeholder="Enter search term…"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
            />
          </div>
        </div>
      </div>

      <div className="panel">
        <div className="panel-head">
          <span className="panel-title">Users list</span>
          <label
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              fontSize: "12.5px",
              color: "var(--ink-faint)",
              fontWeight: 600,
            }}
          >
            Show
            <select
              className="text-input"
              style={{ minWidth: "auto", padding: "7px 10px" }}
              value={entriesPerPage}
              onChange={(e) => {
                setEntriesPerPage(Number(e.target.value));
                setCurrentPage(1);
              }}
            >
              <option value={5}>5</option>
              <option value={10}>10</option>
              <option value={20}>20</option>
              <option value={50}>50</option>
            </select>
          </label>
        </div>

        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th>#</th>
                <th>User</th>
                <th>Email</th>
                <th>Role</th>
                <th>Status</th>
                <th>Last access</th>
                <th>Assigned</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {currentEntries.length === 0 ? (
                <tr>
                  <td colSpan="8">
                    <div className="empty-note">No users found.</div>
                  </td>
                </tr>
              ) : (
                currentEntries.map((user, index) => {
                  const status = (user.status || "inactive").toLowerCase();
                  const viewer = isViewerRole(user.role);
                  return (
                    <tr key={user.user_id || index}>
                      <td>{indexOfFirstEntry + index + 1}</td>
                      <td>
                        <div className="cell-title">{user.name}</div>
                        <div className="cell-sub">{getHandle(user)}</div>
                      </td>
                      <td>{user.email}</td>
                      <td>
                        <em className={roleChipClass(user.role)}>{formatLabel(user.role)}</em>
                      </td>
                      <td>
                        <em className={statusChipClass(status)}>
                          <span className="chip-dot" aria-hidden="true" />
                          {formatLabel(status)}
                        </em>
                      </td>
                      <td>{formatLastAccess(user)}</td>
                      <td>
                        {viewer ? (
                          <em className="chip chip-primary">{assignedBookCount(user)}</em>
                        ) : (
                          <span className="ul-muted">—</span>
                        )}
                      </td>
                      <td className="ul-actions-cell">
                        <div className="row-actions ul-row-actions">
                          <button type="button" className="mini-btn" onClick={() => handleViewUser(user.user_id)}>
                            View
                          </button>
                          <button type="button" className="mini-btn" onClick={() => openEditUser(user.user_id)}>
                            Edit
                          </button>
                          <button type="button" className="mini-btn danger" onClick={() => handleDeleteUser(user.user_id)}>
                            Delete
                          </button>
                          <button type="button" className="mini-btn warn" onClick={() => handleSuspendUser(user)}>
                            {status === "active" ? "Suspend" : "Activate"}
                          </button>
                          {viewer ? (
                            <Link
                              to={`/admin/manage-access?user=${user.user_id}`}
                              className="mini-btn ul-access-btn"
                              title="Manage Access"
                            >
                              Access
                            </Link>
                          ) : (
                            <button type="button" className="mini-btn ul-access-btn is-disabled" disabled title="Only available for viewers">
                              Access
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {filteredUsers.length > 0 && (
          <div className="pagination">
            <span className="page-info">
              Showing {indexOfFirstEntry + 1} to {Math.min(indexOfLastEntry, filteredUsers.length)} of{" "}
              {filteredUsers.length} entries
            </span>
            {filteredUsers.length > entriesPerPage && (
              <>
                <button
                  type="button"
                  className="page-btn"
                  style={navBtnStyle}
                  onClick={() => paginate(currentPage - 1)}
                  disabled={currentPage === 1}
                >
                  Previous
                </button>
                {[...Array(totalPages)].map((_, i) => (
                  <button
                    type="button"
                    key={i}
                    className={`page-btn ${currentPage === i + 1 ? "active" : ""}`}
                    onClick={() => paginate(i + 1)}
                  >
                    {i + 1}
                  </button>
                ))}
                <button
                  type="button"
                  className="page-btn"
                  style={navBtnStyle}
                  onClick={() => paginate(currentPage + 1)}
                  disabled={currentPage === totalPages}
                >
                  Next
                </button>
              </>
            )}
          </div>
        )}
      </div>

      {viewedUser && (
        <div className="ul-modal-overlay" onClick={() => { setViewedUser(null); setError(null); }}>
          <div className="ul-modal" onClick={(e) => e.stopPropagation()}>
            <div className="ul-modal-head">
              <div>
                <p className="ul-modal-kicker">Manage User</p>
                <h2>User Details</h2>
              </div>
              <button
                type="button"
                className="ul-modal-close"
                onClick={() => {
                  setViewedUser(null);
                  setError(null);
                }}
                aria-label="Close"
              >
                <FaTimes />
              </button>
            </div>

            {isLoading ? (
              <div className="ul-modal-state">Loading user details...</div>
            ) : error ? (
              <div className="ul-modal-state error">
                <p>{error}</p>
                <button type="button" className="btn btn-primary" onClick={() => handleViewUser(viewedUser.user_id)}>
                  Retry
                </button>
              </div>
            ) : (
              <>
                <div className="ul-modal-profile">
                  <div>
                    <strong>{viewedUser.name || "User"}</strong>
                    <span>{viewedUser.email || "—"}</span>
                  </div>
                  <div className="ul-modal-pills">
                    <em className={roleChipClass(viewedUser.role)}>{formatLabel(viewedUser.role)}</em>
                    <em className={statusChipClass((viewedUser.status || "").toLowerCase())}>
                      <span className="chip-dot" aria-hidden="true" />
                      {formatLabel(viewedUser.status || "inactive")}
                    </em>
                  </div>
                </div>

                <dl className="ul-modal-grid">
                  <div>
                    <dt>User ID</dt>
                    <dd>{viewedUser.user_id || "—"}</dd>
                  </div>
                  <div>
                    <dt>Organization</dt>
                    <dd>{viewedUser.organization || "—"}</dd>
                  </div>
                  <div>
                    <dt>Designation</dt>
                    <dd>{viewedUser.designation || "—"}</dd>
                  </div>
                  <div>
                    <dt>Country</dt>
                    <dd>{viewedUser.country_name || "—"}</dd>
                  </div>
                  <div>
                    <dt>Created At</dt>
                    <dd>{formatDate(viewedUser.created_at)}</dd>
                  </div>
                  <div>
                    <dt>Last Updated</dt>
                    <dd>{formatDate(viewedUser.updated_at)}</dd>
                  </div>
                </dl>

                <div className="ul-modal-actions">
                  <button
                    type="button"
                    className="ul-modal-btn ghost"
                    onClick={() => {
                      setViewedUser(null);
                      setError(null);
                    }}
                  >
                    Close
                  </button>
                  <button
                    type="button"
                    className="ul-modal-btn primary"
                    onClick={() => openEditUser(viewedUser.user_id)}
                  >
                    Edit User
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {formMode && (
        <div className="ul-modal-overlay" onClick={closeForm}>
          <div className="ul-modal ul-modal-form" onClick={(e) => e.stopPropagation()}>
            <div className="ul-modal-head">
              <div>
                <p className="ul-modal-kicker">Manage User</p>
                <h2>{formMode === "add" ? "Add New User" : "Edit User"}</h2>
              </div>
              <button type="button" className="ul-modal-close" onClick={closeForm} aria-label="Close">
                <FaTimes />
              </button>
            </div>

            <form className="ul-form" onSubmit={handleFormSubmit}>
              <div className="ul-form-grid">
                <label>
                  <span>Name</span>
                  <input name="name" value={formData.name} onChange={handleFormChange} placeholder="Enter full name" required />
                </label>
                <label>
                  <span>Email</span>
                  <input name="email" type="email" value={formData.email} onChange={handleFormChange} placeholder="Enter email" required />
                </label>
                <label>
                  <span>{formMode === "add" ? "Password" : "Password (leave blank to keep)"}</span>
                  <div className="ul-password">
                    <input
                      name="password"
                      type={showPassword ? "text" : "password"}
                      value={formData.password}
                      onChange={handleFormChange}
                      placeholder={formMode === "add" ? "Enter password" : "Enter new password"}
                      required={formMode === "add"}
                    />
                    <button
                      type="button"
                      className="ul-password-toggle"
                      onClick={() => setShowPassword((prev) => !prev)}
                      aria-label="Toggle password"
                    >
                      {showPassword ? <FaEyeSlash /> : <FaEye />}
                    </button>
                  </div>
                </label>
                {formMode === "add" && (
                  <label>
                    <span>Country</span>
                    <select name="country_id" value={formData.country_id} onChange={handleFormChange} required>
                      <option value="">Select Country</option>
                      {countries.map((country) => (
                        <option key={country.country_id || country.id} value={country.country_id || country.id}>
                          {country.country_name}
                        </option>
                      ))}
                    </select>
                  </label>
                )}
                <label>
                  <span>Role</span>
                  <select name="role" value={formData.role} onChange={handleFormChange} required>
                    <option value="viewer">Viewer</option>
                    <option value="admin">Admin</option>
                  </select>
                </label>
                <label>
                  <span>Status</span>
                  <select name="status" value={formData.status} onChange={handleFormChange} required>
                    <option value="active">Active</option>
                    <option value="inactive">Inactive</option>
                  </select>
                </label>
                <label>
                  <span>Organization</span>
                  <input name="organization" value={formData.organization} onChange={handleFormChange} placeholder="Organization" />
                </label>
                <label>
                  <span>Designation</span>
                  <input name="designation" value={formData.designation} onChange={handleFormChange} placeholder="Designation" />
                </label>
              </div>

              {formError && <p className="ul-form-error">{formError}</p>}

              <div className="ul-modal-actions">
                <button type="button" className="ul-modal-btn ghost" onClick={closeForm}>
                  Cancel
                </button>
                <button type="submit" className="ul-modal-btn primary" disabled={formSaving}>
                  {formSaving ? "Saving..." : formMode === "add" ? "Register" : "Save Changes"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
